import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { Form, Container, Button, Segment, Grid, IconProps, Modal, List, Icon, Header, Loader } from "semantic-ui-react";

import { QuestionnaireContainerOwnProps, QuestionnaireTypeProps } from "@teamprevent/common/questionnaires/QuestionnaireContainerProps";
import { Media } from "@teamprevent/common/responsive";
import { ProgressCircle } from "@teamprevent/common/progress-circle";

import { isSectionVisible, renderQuestionnaireSections } from "../utils";
import { ConfirmedButton } from "../../confirmedButton/ConfirmedButton";
import { QuestionnairesState, Section } from "../model";
import { nextSection, openSection } from "../actions";

import { useAutoSave } from "./useAutoSave";
import { QuestionnaireContainerProvider } from "./QuestionnaireContainerContext";

export const ContextAwareProgressBarQuestionnaireContainer = (props: QuestionnaireContainerOwnProps & QuestionnaireTypeProps) => {
    const { loaded, submit, saving, submitting } = useAutoSave(props);
    const [mobileProgressModalOpen, setMobileProgressModalOpen] = React.useState<boolean>(false);
    const openMobileModal = React.useCallback(() => setMobileProgressModalOpen(true), []);
    const closeMobileModal = React.useCallback(() => setMobileProgressModalOpen(false), []);
    const questionnaire = useSelector((state: { questionnaires: QuestionnairesState }) => state.questionnaires[props.questionnaireKey]);
    const activeSection = questionnaire?.sections.find(s => s.active);
    const visibleSections = (questionnaire?.sections || []).filter(isSectionVisible);
    const activeSectionIndex = visibleSections.findIndex(s => s.id === activeSection?.id);
    const progressBarQuestionnaireRef = React.useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();

    const navigateToNextSection = React.useCallback(() => {
        dispatch(nextSection({
            questionnaireKey: props.questionnaireKey,
            sectionId: activeSection?.id
        }));
        progressBarQuestionnaireRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [activeSection?.id, dispatch, props.questionnaireKey]);
    const generateOpenSectionAndCloseMobileProgressModal = (sectionId: string) => {
        return () => {
            dispatch(openSection({
                questionnaireKey: props.questionnaireKey,
                sectionId,
                closeOthers: true
            }));
            setMobileProgressModalOpen(false);
        };
    };
    const getMobileBreadCrumbIcon = (section: Section, index: number): IconProps => {
        if (section.id === activeSection?.id) {
            return { name: "circle", className: "active-section" };
        } else if (index > activeSectionIndex) {
            return { name: "circle", color: "grey" };
        } else if (section.isValid) {
            return { name: "check circle", color: "green" };
        }

        return { name: "times circle", color: "red" };
    };
    const getCssClassForSection = (section: Section, sectionIndex: number): string => {
        const futureSection = sectionIndex > activeSectionIndex;
        const pastSection = sectionIndex < activeSectionIndex;
        const currentSection = sectionIndex === activeSectionIndex;
        if (futureSection) {
            return "empty";
        } else if (!section.isValid && (pastSection || section.isComplete)) {
            return "error";
        } else if (currentSection) {
            return "active";
        } else {
            return "done";
        }
    };

    const renderSubmitButton = () => {
        if (props.readonly) {
            return null;
        }

        return (
            <Container>
                {activeSectionIndex !== visibleSections.length - 1
                    ? <Form.Button
                        type="button"
                        onClick={navigateToNextSection}
                        content="Next"
                    />
                    : !props.hideSubmitButton &&
                    <Form.Field>
                        {props.otherActionButtons}
                        {props.submissionConfirmationText ?
                            <ConfirmedButton
                                content="Submit"
                                className="submit"
                                onClick={submit}
                                promptText={props.submissionConfirmationText}
                                loading={submitting}
                                disabled={submitting}
                                confirmButtonColor="green"
                            /> :
                            <Button
                                content="Submit"
                                className="submit"
                                onClick={submit}
                                loading={submitting}
                                disabled={submitting}
                            />
                        }
                    </Form.Field>
                }
            </Container>
        );
    };

    const renderProgressBreadcrumbBar = () => {
        if (props.readonly) {
            return null;
        }

        return (
            <div ref={progressBarQuestionnaireRef}>
                <Media lessThan="computer">
                    {visibleSections &&
                        <Modal
                            open={mobileProgressModalOpen}
                            onClose={closeMobileModal}
                            trigger={
                                <Segment className="step-segment" basic floated="right">
                                    <Button size="mini" type="button" onClick={openMobileModal}>
                                        <Icon name="bars" />
                                        Step {activeSectionIndex + 1} of {visibleSections.length}
                                    </Button>
                                </Segment>
                            }
                            size="small"
                            closeIcon
                            className="mobile-questionnaire-progress-modal"
                        >
                            <Header content={`Step ${activeSectionIndex + 1} of ${visibleSections.length}`} />
                            <Modal.Content>
                                <List>
                                    {visibleSections.map((s, i) =>
                                        (<List.Item key={s.id} onClick={generateOpenSectionAndCloseMobileProgressModal(s.id)}>
                                            <Icon {...getMobileBreadCrumbIcon(s, i)} />
                                            <List.Content>{s.title}</List.Content>
                                        </List.Item>))}
                                </List>
                            </Modal.Content>
                        </Modal>
                    }
                </Media>
                <Segment as={Media} greaterThanOrEqual="computer" basic>
                    <Grid floated="right" textAlign="center">
                        <Grid.Row columns="equal" className="questionnaire-progress-bar">
                            {visibleSections && visibleSections.map((s, i) =>
                                (<Grid.Column
                                    key={s.id}
                                    onClick={() => dispatch(openSection({ // eslint-disable-line react/jsx-no-bind
                                        questionnaireKey: props.questionnaireKey,
                                        sectionId: s.id,
                                        closeOthers: true
                                    }))}
                                    className={getCssClassForSection(s, i)}
                                >
                                    <ProgressCircle width="15" height="15" />
                                    <div className="title">{i + 1}</div>
                                </Grid.Column>)
                            )}
                        </Grid.Row>
                    </Grid>
                </Segment>
            </div>
        );
    };

    React.useEffect(() => {
        if (questionnaire?.sections?.some(s => s.active) !== true) {
            const firstSection = questionnaire?.sections?.find(isSectionVisible);
            if (firstSection) {
                dispatch(openSection({
                    questionnaireKey: props.questionnaireKey,
                    sectionId: firstSection.id,
                    closeOthers: true
                }));
            }
        }
    }, [dispatch, props.questionnaireKey, questionnaire?.sections]);

    if (props.loading || loaded === false) {
        return (
            <Loader active />
        );
    }

    return (
        <QuestionnaireContainerProvider questionnaireKey={props.questionnaireKey} readonly={props.readonly} extraQuestionContent={props.extraQuestionContent}>
            <Container>
                <Form className="questionnaire">
                    <Segment basic>
                        {!props.hideBreadcrumbBar &&
                            renderProgressBreadcrumbBar()
                        }
                        {renderQuestionnaireSections(props, props.children)}
                    </Segment>
                    {renderSubmitButton()}
                    {saving && <Segment textAlign="center" className="floating-loading-bar">
                        <Header>Saving <Icon loading name="spinner" /></Header>
                    </Segment>}
                </Form>
            </Container>
        </QuestionnaireContainerProvider>
    );
};
